"use client";
import React from "react";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import { CustomLinearProgressTop } from "~/components/loading/Progress.jsx";
import Head from "next/head";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { faWarehouseFull } from "@fortawesome/pro-solid-svg-icons";
import TurnrSupportEmail from "~/components/links/TurnrSupportEmail.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import BrandOrMerchantNeededWrapper from "~/components/BrandOrMerchantNeededWrapper.jsx";
import { serviceIsLoading, useServiceHook } from "rylak-common";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import { fetchEntitiesAll, fetchEntityActions } from "~/services";
import { ENTITY_TYPE } from "~/constants";
import { extractCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";
import EntityActionButton from "~/components/entityAction/EntityActionButton.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import WmsLogoIcon from "~/components/icons/WmsLogoIcon.jsx";
import { getWmsName } from "~/cores/shipmentCore";
import { getBrandName } from "~/cores/brandCore";
import IntegrationCard from "../shipment/IntegrationCard.js";

export default function WarehouseIntegrationPage() {
    const { appProps } = useAppProps();

    const { userData } = appProps;

    let { serviceData: wmsIntegrationsService, fetchServiceData: fetchWmsIntegrationsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitiesAll,
    });

    let { serviceData: wmsIntegrationsActions, fetchServiceData: fetchWmsIntegrationsActionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntityActions,
    });

    function triggerWmsIntegrationsFetch() {
        fetchWmsIntegrationsFn(
            {
                entityType: ENTITY_TYPE.WMS_INTEGRATION,
                sort: "id,asc",
            },
            { silentFetch: true },
        );
    }

    let triggerWmsIntegrationsActionsFetch = () => {
        fetchWmsIntegrationsActionsFn(
            {
                entityType: ENTITY_TYPE.WMS_INTEGRATION,
            },
            { silentFetch: true },
        );
    };

    function onSuccessfullySaved() {
        triggerWmsIntegrationsFetch();
    }

    useRunCallbackIfValueDeeplyChanged(userData, () => {
        triggerWmsIntegrationsFetch();
        triggerWmsIntegrationsActionsFetch();
    });

    const [createAction, filteredActions] = extractCreateEntityAction(wmsIntegrationsActions?.data);

    return (
        <>
            <Head>
                <title>Warehouse Integration</title>
            </Head>
            <BrandOrMerchantNeededWrapper appProps={appProps}>
                <PageInformationCard
                    icon={faWarehouseFull}
                    title={"Warehouse Integration"}
                    content={
                        <div>
                            <p>Here you can connect your WMS to Ingrid. When a returned item arrives at your warehouse, the following chain of events is triggered:</p>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "auto 1fr",
                                    gridColumnGap: "10px",
                                    gridRowGap: "5px",
                                }}
                            >
                                <span>1.</span> <span>The item is marked as returned in your WMS. </span>
                                <span>2.</span> <span>Your WMS marks the item as returned in Ingrid through a webhook.</span>
                                <span>3.</span> <span>Ingrid triggers a refund on the order.</span>
                            </div>
                            <p>If your WMS is missing, don't hesitate to contact us. In the meantime you can go ahead and use Ingrid Quality Check for manually marking items as returned.</p>
                            <p style={{ marginBottom: "0px" }}>
                                You can contact us for help at <TurnrSupportEmail />, or by using our chat option.
                            </p>
                        </div>
                    }
                />
                <TurnrGrid container spacing={2}>
                    <TurnrGrid container item spacing={3} alignItems={"center"}>
                        {createAction && (
                            <TurnrGrid item>
                                <EntityActionButton
                                    appProps={appProps}
                                    entityAction={createAction}
                                    onSuccess={onSuccessfullySaved}
                                    buttonProps={{
                                        variant: "outlined",
                                        size: "medium",
                                    }}
                                />
                            </TurnrGrid>
                        )}
                        <TurnrGrid item>
                            <EntityActionsDotsMenu hideIfEmpty={true} appProps={appProps} entityActions={filteredActions} />
                        </TurnrGrid>
                    </TurnrGrid>
                    <ServiceErrorAlert serviceData={wmsIntegrationsService} />
                    {serviceIsLoading(wmsIntegrationsService) && <CustomLinearProgressTop />}
                    <TurnrGrid item container spacing={2}>
                        {wmsIntegrationsService?.data?.map((integration) => {
                            let { id, type, merchantBrand, shopGroup } = integration;
                            let subtitle = merchantBrand ? getBrandName(merchantBrand) : shopGroup?.name || null;
                            return (
                                <IntegrationCard
                                    appProps={appProps}
                                    key={id}
                                    entity={integration}
                                    title={getWmsName({ type })}
                                    subtitle={subtitle}
                                    icon={<WmsLogoIcon type={type} height={30} />}
                                    onSuccess={onSuccessfullySaved}
                                />
                            );
                        })}
                    </TurnrGrid>
                </TurnrGrid>
            </BrandOrMerchantNeededWrapper>
        </>
    );
}
